import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useInView } from 'react-intersection-observer';
import { ArticleHero } from 'components/ArticleHero/ArticleHero';
import { RichText } from 'components/RichText/RichText';
import { AuthorFeature } from 'components/AuthorFeature/AuthorFeature';
import { Article as ArticleType } from 'data/SingleArticle/types';
import { UpNext } from 'components/Cards/UpNext/UpNext';
import { FloatingPopup } from 'components/Popups/FloatingPopup';
import { SubscribePopup } from 'components/Popups/Subscribe/SubscribePopup';
import { SignupPopup } from 'components/Popups/Signup/SignupPopup';
import { conditionalPopupCategories } from 'constants/popups';
import { mediaQueries } from 'components/mediaQueries';
import { SubscribeMini } from 'components/Subscribe/Mini/SubscribeMini';
import { SubscribeFormContainer } from 'components/Subscribe/Homepage/Container';
import { Spacer } from 'components/Spacer/Spacer';
import { TitleBanner } from 'components/TitleBanner/TitleBanner';
import { Pills } from 'components/Tags/Pills';
import paths from 'constants/paths';

type Props = {
  article: ArticleType;
  sendUpNextArticleSlugToParent: (upNextArticleSlug: string) => void;
};

export default function Article({
  article,
  sendUpNextArticleSlugToParent,
}: Props) {
  const [upNextArticleSlug, setUpNextArticleSlug] = useState('');
  const [subscribeRef, subscribeRefInView] = useInView();
  const [popupIsVisible, setPopupIsVisible] = useState(false);
  const [heroRef, heroRefInView] = useInView();
  const [articleBodyRef, articleBodyRefInView] = useInView();
  const { body, title, author, upNextArticles } = article;

  React.useEffect(() => {
    if (upNextArticleSlug) {
      sendUpNextArticleSlugToParent(upNextArticleSlug);
    }
  }, [upNextArticleSlug]);

  React.useEffect(() => {
    if (
      !heroRefInView &&
      !subscribeRefInView &&
      articleBodyRefInView &&
      !popupIsVisible
    ) {
      setPopupIsVisible(true);
    } else if (popupIsVisible) {
      setPopupIsVisible(false);
    }
  }, [heroRefInView, subscribeRefInView, articleBodyRefInView]);

  const renderConditionalPopup = () => {
    const categoryNames = article.categories.map(
      (category) => category.categoryName,
    );

    if (
      categoryNames.some((c) =>
        conditionalPopupCategories.SubscribePopup.includes(c),
      )
    ) {
      return (
        <FloatingPopup
          isVisible={popupIsVisible}
          setIsVisible={setPopupIsVisible}
        >
          <SubscribePopup />
        </FloatingPopup>
      );
    } else if (
      categoryNames.some((c) =>
        conditionalPopupCategories.SignupPopup.includes(c),
      )
    ) {
      return (
        <FloatingPopup
          isVisible={popupIsVisible}
          setIsVisible={setPopupIsVisible}
        >
          <SignupPopup />
        </FloatingPopup>
      );
    }
  };

  /**
   * Filter out tags and mentioned apps that don't have slugs.
   * Not all tags have slugs. A tag having a slug means it should be user-facing / clickable.
   * Arguably this could happen higher up in the data layer or down lower in Pills, but this seems fine for now.
   */
  const articleTags = article.tags.filter((tag) => Boolean(tag.slug));
  const articleMentionedApps = article.integrationsMentioned.filter((app) =>
    Boolean(app.slug),
  );

  return (
    <Outer>
      {!heroRefInView && <TitleBanner title={title} />}
      <ArticleWrapper>
        <div ref={heroRef}>
          <ArticleHero article={article} />
        </div>
        <ArticleBodyWrapper>
          <div ref={articleBodyRef}>
            <RichText body={body} persona={article.personas[0]} />
          </div>
          <Spacer hasLine={true} />
          <div ref={subscribeRef}>
            <SubscribeMini>
              <SubscribeFormContainer
                inputBackgroundColor="grey100"
                sectionToTrack="ArticlePageSubscribeArticleBottom"
              />
            </SubscribeMini>
          </div>
          <Spacer hasLine={true} />
          <AuthorFeature author={author[0]} />
          <Spacer hasLine={true} />

          {articleTags.length > 0 && (
            <Pills
              title="tags"
              items={articleTags.map((tag) => ({
                slug: paths.tagsPage(tag.slug, 1),
                title: tag.title,
              }))}
            />
          )}
          {articleMentionedApps.length > 0 && (
            <Pills
              title="mentioned apps"
              items={articleMentionedApps.map((app) => ({
                slug: paths.appsPage(app.slug, 1),
                title: app.title,
              }))}
            />
          )}
          <Spacer hasLine={false} />

          {/**
           * It seems that the `UpNext` article is not being fully re-rendered,
           * which means that the hook is not being fired a second time.
           * That's why the UpNext component is being forced to re-render by changing the
           * key based on the current article slug.
           *
           * https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
           */}
          <UpNext
            article={upNextArticles[0]}
            onUpNextArticleSlugReady={setUpNextArticleSlug}
            slugToExclude={article.slug}
            key={article.slug}
          />
        </ArticleBodyWrapper>
      </ArticleWrapper>
      {renderConditionalPopup()}
    </Outer>
  );
}

const Outer = styled.div`
  grid-template-columns: 1fr;
  display: grid;
  justify-content: center;
  place-items: center;
  position: relative;
  width: 100%;
`;

const ArticleWrapper = styled.div`
  padding: 0 20px;

  ${mediaQueries.tablet} {
    max-width: 600px;
    padding: 0;
  }
  ${mediaQueries.desktop} {
    max-width: 760px;
  }
`;

const ArticleBodyWrapper = styled.div`
  ${mediaQueries.desktop} {
    max-width: 700px;
    margin: 0 auto;
  }
`;
